import { fallback, http, webSocket, type Transport } from 'viem'

/** Free HTTP fallbacks (no API keys). Appended after your URLs unless `VITE_RPC_APPEND_PUBLIC_FALLBACKS=0`. */
const DEFAULT_MAINNET_HTTPS: readonly string[] = [
  'https://eth.drpc.org',
  'https://ethereum-rpc.publicnode.com',
  'https://cloudflare-eth.com',
]

const DEFAULT_SEPOLIA_HTTPS: readonly string[] = [
  'https://ethereum-sepolia-rpc.publicnode.com',
  'https://rpc.sepolia.org',
]

function splitCsv(s: string | undefined): string[] {
  if (!s?.trim()) return []
  return s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
}

function dedupe(urls: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const u of urls) {
    if (!seen.has(u)) {
      seen.add(u)
      out.push(u)
    }
  }
  return out
}

function mergeWithDefaults(
  primary: string[],
  defaults: readonly string[],
  appendDefaults: boolean,
): string[] {
  if (!appendDefaults) return primary.length ? primary : [...defaults]
  return dedupe([...primary, ...defaults.filter((d) => !primary.includes(d))])
}

function envFlag(name: string, defaultTrue: boolean): boolean {
  const v = (import.meta.env as unknown as Record<string, string | undefined>)[name]
  if (v === undefined || v === '') return defaultTrue
  return v !== '0' && String(v).toLowerCase() !== 'false'
}

/** Ordered HTTPS URLs for mainnet (first = preferred for reads). */
export function getMainnetHttpUrls(): string[] {
  const urls = splitCsv(import.meta.env.VITE_MAINNET_RPC_URLS)
  const legacy = import.meta.env.VITE_MAINNET_RPC_URL?.trim()
  const legacyFb = splitCsv(import.meta.env.VITE_MAINNET_RPC_FALLBACKS)

  let combined: string[]
  if (urls.length) {
    combined = urls
  } else if (legacy) {
    combined = dedupe([legacy, ...legacyFb])
  } else {
    combined = []
  }

  const shouldAppend = envFlag('VITE_RPC_APPEND_PUBLIC_FALLBACKS', true)
  return mergeWithDefaults(combined, DEFAULT_MAINNET_HTTPS, shouldAppend)
}

/** Ordered HTTPS URLs for Sepolia. */
export function getSepoliaHttpUrls(): string[] {
  const urls = splitCsv(import.meta.env.VITE_SEPOLIA_RPC_URLS)
  const legacy = import.meta.env.VITE_SEPOLIA_RPC_URL?.trim()
  const legacyFb = splitCsv(import.meta.env.VITE_SEPOLIA_RPC_FALLBACKS)

  let combined: string[]
  if (urls.length) {
    combined = urls
  } else if (legacy) {
    combined = dedupe([legacy, ...legacyFb])
  } else {
    combined = []
  }

  const shouldAppend = envFlag('VITE_RPC_APPEND_PUBLIC_FALLBACKS', true)
  return mergeWithDefaults(combined, DEFAULT_SEPOLIA_HTTPS, shouldAppend)
}

const HTTP_OPTS = { batch: true, timeout: 25_000 } as const

function httpsTransports(urls: string[]) {
  return urls.map((u) => http(u, HTTP_OPTS))
}

/**
 * Public-client transport: try URLs in order (viem `fallback`).
 * Optional `VITE_MAINNET_WS_URL` / `VITE_SEPOLIA_WS_URL` prepends a WebSocket transport when set (subscriptions / lower latency for some wallets).
 */
export function buildMainnetTransport(): Transport {
  const ws = import.meta.env.VITE_MAINNET_WS_URL?.trim()
  const https = httpsTransports(getMainnetHttpUrls())
  const parts: Transport[] = []
  if (ws?.startsWith('wss://')) {
    parts.push(webSocket(ws))
  }
  parts.push(...https)
  if (parts.length === 1) return parts[0]!
  return fallback(parts)
}

export function buildSepoliaTransport(): Transport {
  const ws = import.meta.env.VITE_SEPOLIA_WS_URL?.trim()
  const https = httpsTransports(getSepoliaHttpUrls())
  const parts: Transport[] = []
  if (ws?.startsWith('wss://')) {
    parts.push(webSocket(ws))
  }
  parts.push(...https)
  if (parts.length === 1) return parts[0]!
  return fallback(parts)
}
